import { RefreshCw, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import type { McpServer } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useMcpTools } from "@/features/mcps/hooks/useMcps";

interface Props {
  mcp: McpServer;
  // Callback opcional: ao toggleTool, o pai recebe a lista atual marcada.
  // Pai (McpFormDialog) persiste no PATCH global do botao "Salvar".
  onEnabledToolsChange?: (enabledTools: string[]) => void;
}

export function McpToolsTab({ mcp, onEnabledToolsChange }: Props) {
  const { t } = useTranslation("mcps");
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useMcpTools(mcp.id);

  // ``enabled_tools = null`` no banco significa "todas habilitadas".
  // Em vez de carregar uma "flag mode" que confunde o checkbox, materializamos
  // o set: quando a tool list chega via discovery, pre-populamos o Set com
  // TODAS as tools descobertas se enabled_tools era null. Assim o checkbox
  // sempre le do Set diretamente -- desmarcar funciona naturalmente.
  const [enabled, setEnabled] = useState<Set<string>>(() =>
    new Set(mcp.enabled_tools ?? []),
  );
  // ``hydrated`` evita que reseteemos a selecao do user quando o ``refetch``
  // de tools acontecer (manteria as marcacoes locais).
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // MCP mudou (ex.: outro card no listing). Reset.
    setEnabled(new Set(mcp.enabled_tools ?? []));
    setHydrated(false);
  }, [mcp.id, mcp.enabled_tools]);

  useEffect(() => {
    if (hydrated || !data) return;
    // Primeira vez que data chega: se enabled_tools era null, marca todas.
    // Caso contrario, o Set ja tem o snapshot vindo do banco.
    if (mcp.enabled_tools === null) {
      setEnabled(new Set(data.tools.map((t) => t.name)));
    }
    setHydrated(true);
  }, [data, hydrated, mcp.enabled_tools]);

  function toggleTool(name: string) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      // Avisa o pai com a lista atualizada para que o submit global capture.
      onEnabledToolsChange?.(Array.from(next));
      return next;
    });
  }

  const allDiscovered = data?.tools ?? [];
  const allMarked =
    allDiscovered.length > 0 && enabled.size === allDiscovered.length;

  const [filterText, setFilterText] = useState("");
  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return allDiscovered;
    return allDiscovered.filter((tool) => {
      if (tool.name.toLowerCase().includes(q)) return true;
      if (tool.description && tool.description.toLowerCase().includes(q))
        return true;
      return false;
    });
  }, [allDiscovered, filterText]);

  function selectAllVisible() {
    setEnabled((prev) => {
      const next = new Set(prev);
      for (const tool of filtered) next.add(tool.name);
      onEnabledToolsChange?.(Array.from(next));
      return next;
    });
  }

  function deselectAllVisible() {
    setEnabled((prev) => {
      const next = new Set(prev);
      for (const tool of filtered) next.delete(tool.name);
      onEnabledToolsChange?.(Array.from(next));
      return next;
    });
  }

  return (
    <div className="space-y-4 py-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {t("toolsTab.discoveredHint")}{" "}
          {allMarked ? t("toolsTab.allEnabled") : null}
        </p>
        <div className="flex items-center gap-2">
          {data ? (
            <Badge variant="outline">
              {data.cached ? t("toolsTab.cache") : t("toolsTab.fresh")}
            </Badge>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void refetch()}
            disabled={isLoading}
          >
            <RefreshCw className="h-4 w-4" />
            {t("toolsTab.reload")}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : isError ? (
        <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm">
          {t("toolsTab.discoveryError")}{" "}
          {(error as Error)?.message ?? t("toolsTab.discoveryErrorFallback")}
        </div>
      ) : allDiscovered.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("toolsTab.noTools")}
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {t("toolsTab.availableCount", { count: allDiscovered.length })}{" "}
            {allMarked
              ? t("toolsTab.allChecked")
              : enabled.size > 0
                ? t("toolsTab.someChecked", { count: enabled.size })
                : t("toolsTab.noneChecked")}{" "}
            <Trans
              ns="mcps"
              i18nKey="toolsTab.saveHint"
              components={{ strong: <strong /> }}
            />
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-400" />
              <Input
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder={t("toolsTab.filter.placeholder")}
                className="pl-8 text-sm"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={selectAllVisible}
              disabled={filtered.length === 0}
            >
              {t("toolsTab.actions.selectAllVisible")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={deselectAllVisible}
              disabled={filtered.length === 0}
            >
              {t("toolsTab.actions.deselectAllVisible")}
            </Button>
          </div>
          {filtered.length === 0 ? (
            <p className="rounded-md border border-dashed py-4 text-center text-xs text-muted-foreground">
              {t("toolsTab.filter.noResults")}
            </p>
          ) : (
            <ul className="max-h-[24rem] divide-y overflow-y-auto rounded-md border">
              {filtered.map((tool) => (
                <li
                  key={tool.name}
                  className="flex items-start gap-3 px-3 py-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={enabled.has(tool.name)}
                    onChange={() => toggleTool(tool.name)}
                    className="mt-1 shrink-0"
                    aria-label={t("toolsTab.enableAria", { name: tool.name })}
                  />
                  <div className="flex min-w-0 flex-col">
                    <span className="break-all font-mono text-xs font-medium">
                      {tool.name}
                    </span>
                    {tool.description ? (
                      <span className="line-clamp-2 text-xs text-muted-foreground">
                        {tool.description}
                      </span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
